/**
 * The order and dispatch calls the console makes.
 *
 * Thin wrappers over `request`, typed with the generated schema's own types.
 * They exist so components never build a URL or remember a method — the kind of detail
 * that drifts once three screens each call the same endpoint their own way.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

export type QueuedOrder = Schema<'QueuedOrder'>;

/**
 * The automation's state for one order, as the queue shows it.
 *
 * Auto-dispatch a dispatcher cannot see is auto-dispatch a dispatcher does not trust, and
 * the first thing an office does with a system it does not trust is stop using it.
 */
export type AutoDispatchState = Schema<'AutoDispatchState'>;

export type OrderHistoryEntry = Schema<'OrderHistoryEntry'>;

export type OrderDetail = Schema<'OrderDetail'>;

/** One line of an itemised fare, as a code the console owns the words for. */
export type FareLine = Schema<'FareLine'>;

export type Customer = Schema<'CustomerView'>;

/** What the firm's drivers have said about a caller, for the screen-pop. */
export type CustomerStanding = Schema<'CustomerStanding'>;

export type AddressSuggestion = Schema<'AddressSuggestion'>;

export type GeocodeResult = Schema<'GeocodeResult'>;

/** A street a search found, as its numbers are asked for by. */
export type StreetRef = Schema<'StreetRef'>;

/** One number on a street. */
export type StreetNumber = Schema<'StreetNumber'>;

/** Bookings to take off Адреси за заключване, or to put back. */
export type BookingsDismiss = Schema<'BookingsDismiss'>;

/** How many pickups and destinations a dismissal, or taking one back, changed. */
export type BookingsDismissed = Schema<'BookingsDismissed'>;

/** What deleting imported addresses did, with why each of the rest was kept. */
export type ImportedAddressesRemoved = Schema<'ImportedAddressesRemoved'>;

export const ordersApi = {
  queue: (unassignedOnly = false) =>
    request<QueuedOrder[]>(`/api/dispatch/queue?unassignedOnly=${unassignedOnly}`),

  get: (orderId: string) => request<OrderDetail>(`/api/orders/${orderId}`),

  create: (body: {
    customerPhone?: string | null;
    customerName?: string | null;
    pickupAddress: string;
    pickupLongitude: number;
    pickupLatitude: number;
    pickupNotes?: string | null;
    destinationAddress?: string | null;
    destinationLongitude?: number | null;
    destinationLatitude?: number | null;
    passengerCount?: number;

    /**
     * ISO instant the car is wanted, for a booking taken in advance.
     *
     * Omitted for the overwhelming majority of calls, which are for now. A caller ringing
     * up to book tomorrow's airport run is the case this exists for.
     */
    scheduledFor?: string | null;

    /**
     * The call this order was taken on.
     *
     * Carried so the server can tie the two together and clear the call from the
     * missed-call queue in the same request. A second request afterwards to say where the
     * order came from is a second request a client can forget, and forgetting it means a
     * colleague rings the customer back to offer them the car that is already on its way.
     */
    fromCallId?: string | null;

    /**
     * The firm's own address the pickup was chosen from (#420). The server puts the firm's
     * point in the pickup and copies its note for the driver; without it an address that
     * reads the same as one is still matched.
     */
    pickupPlaceId?: string | null;

    /** The class the car must be, or null for any (#369). */
    vehicleCategory?: string | null;

    /** What the car must offer, every one of them. */
    vehicleFeatures?: string[];
  }) => request<OrderDetail>('/api/orders', { method: 'POST', body }),

  cancel: (orderId: string, reason?: string) =>
    request<void>(`/api/orders/${orderId}/cancel`, { method: 'POST', body: { reason } }),

  /**
   * Takes a row of Адреси за заключване off the list by stamping exactly these bookings — or,
   * with `dismissed: false`, puts them back. Nothing else about the orders changes. The
   * administrator's alone.
   */
  dismissAddresses: (body: BookingsDismiss) =>
    request<BookingsDismissed>('/api/orders/addresses/dismiss', { method: 'POST', body }),
};

export const customersApi = {
  standing: (customerId: string) =>
    request<CustomerStanding>(`/api/customers/${customerId}/standing`),

  /**
   * Looks a caller up by number.
   *
   * Returns undefined for an unknown number rather than throwing: the console asks this
   * on every call, most of which are from people who have never rung before, so "not
   * found" is the ordinary answer.
   */
  byPhone: (phone: string) =>
    request<Customer | undefined>(`/api/customers/by-phone/${encodeURIComponent(phone)}`),

  addresses: (customerId: string) =>
    request<AddressSuggestion[]>(`/api/customers/${customerId}/addresses`),

  searchAddresses: (customerId: string, query: string) =>
    request<AddressSuggestion[]>(
      `/api/customers/${customerId}/addresses/search?q=${encodeURIComponent(query)}`,
    ),

  /**
   * Deletes imported addresses nobody here has used, for good, leaving a tombstone so the same
   * file imported again does not bring them back. The rest are kept, with why. The
   * administrator's alone.
   */
  removeImportedAddresses: (addressIds: string[]) =>
    request<ImportedAddressesRemoved>('/api/customers/addresses/remove', { method: 'POST', body: { addressIds } }),
};

/**
 * How far an address search reaches (#382): the firm's town, its villages too, or further.
 * Lower case because it travels in a query string, where the API reads it by name.
 */
export const searchScopes = ['town', 'nearby', 'wider'] as const;

export type SearchScope = (typeof searchScopes)[number];

/**
 * A position of the desk's switch: one of the levels, or `auto` — the town first, and the next
 * level out only when the one before found nothing.
 */
export type SearchMode = SearchScope | 'auto';

/**
 * What a search at one position found. `mapUnavailable` says our own map search was not
 * answering, so the results are the firm's own places alone.
 */
export type AddressAnswer = Schema<'AddressAnswer'>;

/** What the automatic position found, and at which level; null when the firm drew no town. */
export type WideningSearch = Narrow<Schema<'WideningSearch'>, { level: SearchScope | null }>;

/** Which of the three levels this firm has drawn. An undrawn `wider` means anywhere. */
export type DrawnLevels = Schema<'DrawnLevels'>;

export const geoApi = {
  /**
   * Unbounded without a scope — the town lookup on Settings → Map wants exactly that. With
   * `numbers`, a street found alone comes with its first numbers under it.
   */
  search: (query: string, scope?: SearchScope, numbers = false) =>
    request<AddressAnswer>(
      `/api/geo/search?q=${encodeURIComponent(query)}${scope === undefined ? '' : `&scope=${scope}`}`
        + (numbers ? '&numbers=true' : ''),
    ),

  /** The automatic position, widened on the server so it is one request however far it goes. */
  searchWidening: (query: string, numbers = false) =>
    request<WideningSearch>(
      `/api/geo/search/auto?q=${encodeURIComponent(query)}${numbers ? '&numbers=true' : ''}`,
    ),

  /** Every number on a street a search found, in the order they are counted. */
  streetNumbers: (street: StreetRef, at: { longitude: number; latitude: number }, scope?: SearchScope) => {
    const query = new URLSearchParams({
      name: street.name,
      town: street.town,
      lon: String(at.longitude),
      lat: String(at.latitude),
    });

    if (street.quarter != null) query.set('quarter', street.quarter);
    if (scope !== undefined) query.set('scope', scope);

    return request<StreetNumber[]>(`/api/geo/street-numbers?${query.toString()}`);
  },

  levels: () => request<DrawnLevels>('/api/geo/search-levels'),
};

/**
 * Turns a .NET TimeSpan into minutes.
 *
 * Serialised as `hh:mm:ss.fffffff`, optionally with a leading `d.` and optionally
 * negative — a pre-booking that is not yet due reads as negative waiting time, which
 * the queue shows as "due in" rather than as an order someone has forgotten.
 */
export function timeSpanToMinutes(value: string): number {
  const negative = value.startsWith('-');
  const [days, clock] = negative ? value.slice(1).split(/\.(?=\d{2}:)/) : value.split(/\.(?=\d{2}:)/);

  const parts = (clock ?? days).split(':').map(Number);
  const dayPart = clock === undefined ? 0 : Number(days);

  const minutes = dayPart * 1440 + (parts[0] ?? 0) * 60 + (parts[1] ?? 0) + (parts[2] ?? 0) / 60;

  return negative ? -minutes : minutes;
}

export type DriverCandidate = Schema<'DriverCandidate'>;

/**
 * The arithmetic behind a candidate's place in the list.
 *
 * Everything is in metres on purpose, so it can be read as one sentence: this is how far
 * away they were, and this is how far away we treated them as being.
 */
export type CandidateScore = Schema<'CandidateScore'>;

/** One offer that was made on an order, for the "why did it pick him" panel. */
export type OfferAttempt = Schema<'OfferAttempt'>;

/** A ride booked for later, as the console lists it. */
export type PreBooking = Schema<'PreBooking'>;

export const dispatchApi = {
  candidates: (orderId: string) =>
    request<DriverCandidate[]>(`/api/dispatch/orders/${orderId}/candidates`),

  attempts: (orderId: string) =>
    request<OfferAttempt[]>(`/api/dispatch/orders/${orderId}/attempts`),

  preBookings: () => request<PreBooking[]>('/api/dispatch/prebookings'),

  setAutomatic: (orderId: string, automatic: boolean) =>
    request<void>(`/api/dispatch/orders/${orderId}/automatic`, {
      method: 'POST',
      body: { automatic },
    }),

  assign: (orderId: string, driverId: string) =>
    request<Schema<'DispatchAssignment'>>(`/api/dispatch/orders/${orderId}/assign`, {
      method: 'POST',
      body: { driverId },
    }),
};

/**
 * Distance as a dispatcher would say it aloud.
 *
 * Metres below a kilometre, then one decimal — "1.4 km" is what gets said on the radio,
 * and "1437 m" is a number nobody repeats correctly.
 */
export function formatDistance(metres: number, locale: string): string {
  if (metres < 1000) {
    return `${Math.round(metres / 10) * 10} m`;
  }

  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(metres / 1000)} km`;
}
